import { useEffect, useRef, useState } from "react";
import HeartbeatPlayerEngine from "./HeartbeatPlayerEngine";
import { buildPrefillUrl } from "../buildPrefillUrl";

const STORAGE_KEY = "hb_survey_v1";

// Order matters: baseline first, then calm/slow boundary, then energising/fast boundary
const STEPS = [
  { key: "standard", label: "Standard", question: "Which setting feels standard or neutral to you?" },
  { key: "calming", label: "Calming", question: "Which setting feels calming?" },
  { key: "too_slow", label: "Too slow (uncomfortable)", question: "Which setting feels too slow to be comfortable?" },
  { key: "exciting", label: "Energising", question: "Which setting feels energising or activating?" },
  { key: "too_fast", label: "Too fast (uncomfortable)", question: "Which setting feels too fast to be comfortable?" },
];

function clamp(v, lo, hi) {
  return Math.min(hi, Math.max(lo, v));
}

export default function HeartbeatSurveyWizard({ onExit }) {
  // screen: -1 = intro, 0..STEPS-1 = questions, STEPS.length = completion
  const [screen, setScreen] = useState(-1);

  // current adjustment values (carry across steps)
  const [bpm, setBpm] = useState(54);
  const [amp, setAmp] = useState(50);

  // saved answers: { [key]: { bpm, amp } }
  const [answers, setAnswers] = useState({});

  // play state owned by wizard
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef(null);

  const [formUrl, setFormUrl] = useState(null); // when set, we show embedded form

  // intro gate
  const [eligibleConfirmed, setEligibleConfirmed] = useState(false);

  // load from localStorage on mount
  useEffect(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw);

      if (parsed?.bpm) setBpm(parsed.bpm);
      if (parsed?.amp) setAmp(parsed.amp);
      if (parsed?.answers) setAnswers(parsed.answers);
      if (typeof parsed?.screen === "number") setScreen(parsed.screen);
      if (typeof parsed?.eligibleConfirmed === "boolean") setEligibleConfirmed(parsed.eligibleConfirmed);
    } catch {
      // ignore
    }
  }, []);

  // save to localStorage whenever state changes
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ screen, bpm, amp, answers, eligibleConfirmed })
      );
    } catch {
      // ignore
    }
  }, [screen, bpm, amp, answers, eligibleConfirmed]);

  // If user changes screen away from questions, stop audio
  useEffect(() => {
    if (screen === -1 || screen === STEPS.length) {
      if (isPlaying) handleStop();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  async function handlePlay() {
    if (!playerRef.current) return;
    await playerRef.current.play();
    setIsPlaying(true);
  }

  function handleStop() {
    if (!playerRef.current) return;
    playerRef.current.stop();
    setIsPlaying(false);
  }

  async function togglePlay() {
    if (isPlaying) handleStop();
    else await handlePlay();
  }

  function goBackToWelcome() {
    handleStop();
    if (typeof onExit === "function") onExit();
    else setScreen(-1);
  }

  function startSurvey() {
    if (!eligibleConfirmed) return;
    setScreen(0);
  }

  function saveCurrentStepAndReturn() {
    const step = STEPS[screen];
    if (!step) return answers;

    const nextAnswers = {
      ...answers,
      [step.key]: { bpm, amp },
    };

    setAnswers(nextAnswers);
    return nextAnswers;
  }

  function next() {
    if (screen === -1) {
      startSurvey();
      return;
    }

    if (screen >= 0 && screen < STEPS.length) {
      const nextAnswers = saveCurrentStepAndReturn();
      const nextScreen = screen + 1;

      if (nextScreen >= STEPS.length) {
        setScreen(STEPS.length); // completion
        const url = buildPrefillUrl(nextAnswers, { field: "bpm" });
        setFormUrl(url);
        handleStop();
      } else {
        setScreen(nextScreen);
      }
    }
  }

  function back() {
    if (screen < 0) return;

    // If they are on first question, go back to intro (do not reset everything)
    if (screen === 0) {
      handleStop();
      setScreen(-1);
      return;
    }

    setScreen(screen - 1);
  }

  function resetAll() {
    handleStop();

    setFormUrl(null);
    setScreen(-1);
    setBpm(54);
    setAmp(50);
    setAnswers({});
    setIsPlaying(false);
    setEligibleConfirmed(false);

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore
    }
  }

  const isIntro = screen === -1;
  const isComplete = screen === STEPS.length;
  const inQuestions = screen >= 0 && screen < STEPS.length;

  const questionText = inQuestions ? STEPS[screen].question : "";

  // Optional: show previously saved value when you land on a step
  useEffect(() => {
    if (!inQuestions) return;
    const step = STEPS[screen];
    const saved = answers[step.key];
    if (saved) {
      setBpm(clamp(saved.bpm, 30, 150));
      setAmp(clamp(saved.amp, 1, 100));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen]);

  return (
    <div className="app">
      <img
        src="logo_no_neurotech.png"
        alt="LYEONS Logo"
        className="lyeons-logo"
        style={{ paddingTop: "32px" }}
      />

      <h2 className="title">HEARTBEAT STUDY</h2>

     {isIntro && (
  <div style={{ maxWidth: 520, padding: "0 24px", boxSizing: "border-box" }}>
    <p className="subtitle" style={{ maxWidth: "100%", marginBottom: 14 }}>
      <strong>About this study</strong>
      <br />
      You will explore how different heartbeat rhythms feel by adjusting a heartbeat sound. We are interested in what feels
      neutral, calming, energising, or uncomfortable to you. This is not medical treatment and does not provide diagnosis
      or medical advice. You may stop at any time.
    </p>

    <div
      style={{
        padding: 12,
        borderRadius: 12,
        background: "rgba(255,255,255,0.04)",
        textAlign: "left",
        margin: "0 auto",
        maxWidth: 520,
      }}
    >
      <div style={{ fontSize: 13, color: "#6e6a66", lineHeight: 1.5 }}>
        <strong>Who can take part</strong>
        <ul style={{ marginTop: 8, marginBottom: 10, paddingLeft: 18 }}>
          <li>Aged 18 or over</li>
          <li>Fluent in English</li>
          <li>Generally healthy</li>
          <li>Mild everyday stress or anxiety without a formal diagnosis</li>
        </ul>

        <strong>Please do not take part if you</strong>
        <ul style={{ marginTop: 8, marginBottom: 10, paddingLeft: 18 }}>
          <li>Have a diagnosed mental health condition (for example PTSD, bipolar disorder, psychosis)</li>
          <li>Have severe cardiovascular disease, significant arrhythmia, or a history of fainting with mild stress</li>
          <li>Have an implanted electronic or active medical device (for example a pacemaker)</li>
          <li>Have vibration-triggered epilepsy</li>
          <li>Are pregnant</li>
          <li>Have a known metal allergy or significant skin condition on the wrist or palm</li>
          <li>Have a strong family history of inherited cardiac disease or sudden cardiac death under age 50</li>
        </ul>

        <div style={{ marginTop: 10 }}>
          If you feel unwell, anxious, dizzy, or uncomfortable at any point, please stop and exit the study.
        </div>

        <div style={{ marginTop: 10 }}>
          <strong>Data and privacy</strong>
          <br />
          Your responses are handled in line with UK data protection requirements and LYEONS internal procedures. Data used
          for analysis is pseudonymised and only anonymised data may be shared externally. You will be asked for formal
          consent before submitting your responses.
        </div>
      </div>
    </div>

    <div
      style={{
        marginTop: 12,
        padding: 12,
        borderRadius: 12,
        background: "rgba(255,255,255,0.04)",
        textAlign: "left",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: 520,
      }}
    >
      <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer" }}>
        <input
          type="checkbox"
          checked={eligibleConfirmed}
          onChange={(e) => setEligibleConfirmed(e.target.checked)}
          style={{ marginTop: 4 }}
        />
        <span style={{ fontSize: 13, color: "#6e6a66" }}>
          I confirm that I have read the information above, meet the eligibility criteria, and would like to take part in
          this study.
        </span>
      </label>
    </div>

    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 24, // match welcome screen spacing
        marginTop: 16,
        alignItems: "center",
        width: "100%",
      }}
    >
      <button
        className="mode-select-button"
        onClick={startSurvey}
        disabled={!eligibleConfirmed}
        style={{
          opacity: eligibleConfirmed ? 1 : 0.5,
          cursor: eligibleConfirmed ? "pointer" : "not-allowed",
        }}
      >
        START
      </button>

      <button className="mode-select-button" onClick={goBackToWelcome}>
        BACK
      </button>
    </div>
  </div>
)}


      {inQuestions && (
        <>
          <div style={{ maxWidth: 520, margin: "0 auto", padding: "0 24px", textAlign: "center" }}>
            <h3 style={{ marginTop: 8 }}>{questionText}</h3>
            <p style={{ opacity: 0.7, marginTop: 6 }}>
              {screen + 1} of {STEPS.length}
            </p>
          </div>

          <HeartbeatPlayerEngine
            ref={playerRef}
            bpm={bpm}
            amplitude={amp}
            onBpmChange={setBpm}
            onAmplitudeChange={setAmp}
          />

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: 12,
              marginTop: 8,
              paddingBottom: 24,
            }}
          >
            <button className="update-btn" onClick={back}>
              BACK
            </button>

            <button className="update-btn" onClick={togglePlay}>
              {isPlaying ? "STOP" : "PLAY"}
            </button>

            <button className="update-btn" onClick={next}>
              {screen === STEPS.length - 1 ? "FINISH" : "NEXT"}
            </button>
          </div>
        </>
      )}

      {formUrl && (
        <div className="form-breakout">
          <div className="form-container">
            <h3>Final details</h3>
            <p style={{ opacity: 0.8 }}>Please submit your responses below.</p>

            <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,255,255,0.15)" }}>
              <iframe
                title="Heartbeat survey submission"
                src={formUrl}
                width="100%"
                height="900"
                frameBorder="0"
                marginHeight="0"
                marginWidth="0"
              >
                Loading…
              </iframe>
            </div>

            <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 16 }}>
              <button className="update-btn" onClick={() => setFormUrl(null)}>
                BACK TO SUMMARY
              </button>
              <button className="update-btn" onClick={resetAll}>
                RESET
              </button>
            </div>
          </div>
        </div>
      )}

      {isComplete && !formUrl && (
        <div style={{ maxWidth: 640, margin: "0 auto", padding: 24 }}>
          <h3>Complete</h3>
          <p style={{ opacity: 0.8 }}>Below is what was saved for each category.</p>

          <div style={{ marginTop: 12 }}>
            {STEPS.map((step) => {
              const a = answers[step.key];
              return (
                <div
                  key={step.key}
                  style={{
                    padding: 12,
                    borderRadius: 12,
                    marginBottom: 10,
                    background: "rgba(255,255,255,0.04)",
                  }}
                >
                  <strong>{step.label}</strong>
                  <div style={{ opacity: 0.85, marginTop: 4 }}>
                    {a ? (
                      <>
                        {a.bpm} BPM, {a.amp}% strength
                      </>
                    ) : (
                      <>No answer saved</>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <div style={{ display: "flex", gap: 12, justifyContent: "center", marginTop: 16 }}>
            <button className="update-btn" onClick={() => setScreen(0)}>
              REVIEW / EDIT
            </button>
            <button className="update-btn" onClick={resetAll}>
              RESET
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
